import React, { useEffect, useRef, useState } from 'react';
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';

export type RangeSliderProps = {
  min: number;
  max: number;
  step: number;
  tooltipID: string;
  inputID: string;
  defaultValue?: number;
  label?: string;
  disabled?: boolean;
  onSliderChange?: (value: number) => void;
  showRangeLabel?: boolean;
  showPercentSymbol?: boolean;
};

const RangeSlider: React.FC<RangeSliderProps> = (
  {
    min = 0,
    max = 100,
    step = 1,
    tooltipID,
    inputID,
    defaultValue = 0,
    label,
    disabled = false,
    onSliderChange,
    showRangeLabel,
    showPercentSymbol: percentData = false,
  }) => {
  const inputTarget = useRef<HTMLInputElement>(null);
  const [sliderValue, setSliderValue] = useState<number>(defaultValue);
  const [toolTipPosition, setTooltipPosition] = useState(0);

  useEffect(() => {
    // When the Range Slider has a default value
    // we need to measure and position the popover before displaying:
    measurePopoverBeforeFirstShowing();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const measurePopoverBeforeFirstShowing = () => {
    let inputClientWidth = document?.getElementById(inputID)?.clientWidth;
    let thumbnail = document?.getElementById(tooltipID);

    if (thumbnail) {
      let widthForCalculation = thumbnail?.clientWidth;
      calculatePosition(inputClientWidth, widthForCalculation);
      thumbnail!.style!.display = 'none';
    }
  };

  const calculatePosition = (inputCurrentWidth: any, popperCurrentWidth: any) => {
    let value = sliderValue - min;

    // calculations tor thumbnail that becomes off-center
    // if you get closer the edge of the slider
    const halfThumbWidth = 25 / 2; // This thumb(the blue circle handle) width is set in css
    const halfPopperWidth = popperCurrentWidth / 2;
    const inputCenter = inputCurrentWidth / 2;

    // Calculate when is the value positioned as percentage of whole range lenght: 
    let percentOfRangeSliderLength = value / (max - min);
    if (isNaN(percentOfRangeSliderLength)) {
      percentOfRangeSliderLength = 0;
    }

    const valuePositionInPixels = percentOfRangeSliderLength * inputCurrentWidth;
    const distanceFromCenter = valuePositionInPixels - inputCenter;
    const distanceFromCenterInPercents = distanceFromCenter / inputCenter;
    const offset = distanceFromCenterInPercents * halfThumbWidth;
    const finalThumbnailPosition = valuePositionInPixels - halfPopperWidth - offset;

    setTooltipPosition(finalThumbnailPosition);
  };

  const repositionPopover = (e: any) => {
    let value = e.target.value - min;

    // calculations: tor thumbnail that becomes off-center
    // if you get closer the edge of the slider
    const halfThumbWidth = 25 / 2; // This thumb(the blue circle handle) width is set in css
    const halfPopperWidth = 45 / 2; // TODO: get current popover size
    const sliderWidth = e.target.clientWidth;
    const inputCenter = sliderWidth / 2;

    let percentOfRangeSliderLength = value / (max - min);
    if (isNaN(percentOfRangeSliderLength)) {
      percentOfRangeSliderLength = 0;
    }

    const valuePositionInPixels = percentOfRangeSliderLength * sliderWidth;
    const distanceFromCenter = valuePositionInPixels - inputCenter;
    const distanceFromCenterInPercents = distanceFromCenter / inputCenter;
    const offset = distanceFromCenterInPercents * halfThumbWidth;
    const finalThumbnailPosition = valuePositionInPixels - halfPopperWidth - offset;

    setTooltipPosition(finalThumbnailPosition);
  };

  const getBackgroundSize = (value: number) => {
    return { backgroundSize: `${((value - min) * 100) / (max - min)}% 100%` };
  };

  return (
    <div>
      <Row>
        <Col className="text-fs-6">{label}</Col>
        <Col className="col-1 text-fs-6 text-end">
          {showRangeLabel ? sliderValue : ''}
          {percentData ? '%' : ''}
        </Col>
      </Row>
      <div className="pt-7">
        <OverlayTrigger
          placement="top-start"
          trigger="focus"
          defaultShow={true}
          popperConfig={{
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [toolTipPosition, 15],
                },
              },
              {
                name: 'arrow',
                options: {
                  padding: 17,
                },
              },
            ],
          }}
          overlay={<Tooltip id={tooltipID}>{sliderValue}</Tooltip>}
        >
          <input
            className="range-slider"
            id={inputID}
            type="range"
            ref={inputTarget}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            onClick={(e) => {
              repositionPopover(e);
              measurePopoverBeforeFirstShowing();
              document.getElementById(tooltipID)!.style!.display = 'block';
            }}
            onChange={(e) => {
              setSliderValue(Number(e.target.value));
              if (onSliderChange) {
                onSliderChange(Number(e.target.value));
              }
              repositionPopover(e);
              document.getElementById(tooltipID)!.style!.display = 'block';
            }}
            style={getBackgroundSize(sliderValue)}
            value={sliderValue}
          />
        </OverlayTrigger>
      </div>
    </div>
  );
};

export default RangeSlider;
